import { Key, useMemo, useState } from 'react';
import { EditTwoTone } from '@ant-design/icons';
import { TableColumnsType } from 'antd';
import { SIZE, Stack, Text } from '@quinyx/ui';
import { Link } from 'react-router-dom';

import { useCustomerCreationsInProgress } from '../../../../hooks/customer';
import { ICreateCustomerProgress } from '../../../../api/interfaces/customer';
import {
  CUSTOMERROUTE,
  withCustomerLayout,
} from '../../../../layouts/customer';
import { getFilterProps } from '../../../../components/Table/utils';
import { sortString } from '../../../../utils';
import Table from '../../../../components/Table';
import { format } from '../../../../utils/time';
import { DATE_TIME_SECONDS_FORMAT } from '../../../../constants/time';
import { useTranslate } from '../../../../hooks/translate';
import { translateProgressMessage } from '../../../../utils/copy/progressMessages';

const Customers = () => {
  const { t } = useTranslate();
  const [searchText, setSearchText] = useState<Key>('');
  const [searchedColumn, setSearchedColumn] = useState<Key>('');
  const { data = [], isLoading } = useCustomerCreationsInProgress();

  const columns: TableColumnsType<ICreateCustomerProgress> = useMemo(
    () => [
      {
        width: 1,
        title: t('common.edit'),
        key: 'action',
        render: (_, { id }) => (
          <Link
            data-test-id="customer-create-in-progress-link"
            to={`${CUSTOMERROUTE.IN_PROGRESS}/${encodeURIComponent(id)}`}
            style={{ display: 'flex', justifyContent: 'center' }}>
            <EditTwoTone
              style={{ display: 'flex', justifyContent: 'center' }}
            />
          </Link>
        ),
      },
      {
        title: t('customer.customerName'),
        dataIndex: 'domainName',
        key: 'domainName',

        ...getFilterProps(
          'name',
          setSearchText,
          setSearchedColumn,
          searchedColumn,
          searchText,
        ),

        sorter: (a: ICreateCustomerProgress, b: ICreateCustomerProgress) =>
          sortString(a.domainName, b.domainName),
      },

      {
        title: t('customer.receivedAt'),
        dataIndex: 'receivedAt',
        key: 'receivedAt',
        sorter: (a: ICreateCustomerProgress, b: ICreateCustomerProgress) =>
          sortString(a.receivedAt, b.receivedAt),
        render: (timestamp) => format(timestamp, DATE_TIME_SECONDS_FORMAT),
      },
      {
        title: t('customer.currentMessage'),
        dataIndex: 'messages',
        key: 'messages',
        render: (messages: ICreateCustomerProgress['messages']) => {
          const lastMessage = messages.at(-1);
          return lastMessage && translateProgressMessage(lastMessage, t);
        },
      },
    ],
    [t, searchText, searchedColumn],
  );

  const dataSortedLatestFirst = [...data].sort((a, b) =>
    sortString(b.receivedAt, a.receivedAt),
  );

  return (
    <Stack spacing={SIZE.M}>
      <Text variant="titleStandard" color="PrimaryMain">
        {t('customer.customersBeingCreated')}
      </Text>
      <Table
        getRowKey={(progress) => progress.id}
        columns={columns}
        isLoading={isLoading}
        data={dataSortedLatestFirst}
      />
    </Stack>
  );
};

export default withCustomerLayout(Customers);
